import { useEffect, useState } from 'react';
import {
  Download, FileSpreadsheet, FileText, FileType, Image as ImageIcon,
  Loader2, Paperclip, Table, Trash2,
} from 'lucide-react';
import { invoicesApi } from '@/services/api';
import { formatDateTime } from '@/utils/currency';
import { InvoicePdfPreview } from './InvoicePdfPreview';
import { InvoiceImageLightbox } from './InvoiceImageLightbox';
import { ATTACHMENT_ACCEPT } from './gridCells';
import type { InvoiceFile } from '@/types';

/**
 * Anlagen-Karte im Rechnungs-Drawer (Phase 3): mehrere Dateien je
 * Rechnung — Bilder mit Thumbnail + Lightbox, PDFs mit Inline-Vorschau,
 * Office/CSV mit Download. Blob-URLs werden pro Anlage geladen und
 * strikt im Cleanup revoked (kein Cache über den Unmount hinaus).
 */

type MimeGroup = 'image' | 'pdf' | 'sheet' | 'document' | 'other';

const mimeGroup = (mime: string | null, name: string | null): MimeGroup => {
  const m = (mime ?? '').toLowerCase();
  if (m.startsWith('image/')) return 'image';
  if (m.includes('pdf')) return 'pdf';
  if (m.includes('spreadsheet') || m.includes('ms-excel') || /\.(xlsx?|csv)$/i.test(name ?? '')) return 'sheet';
  if (m.includes('wordprocessing') || m.includes('msword') || /\.docx?$/i.test(name ?? '')) return 'document';
  return 'other';
};

const groupIcon: Record<MimeGroup, typeof FileText> = {
  image: ImageIcon,
  pdf: FileText,
  sheet: FileSpreadsheet,
  document: FileType,
  other: Table,
};

const formatFileSize = (bytes: number | null): string => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface InvoiceAttachmentsProps {
  invoiceId: number;
  attachments: InvoiceFile[];
  /** admin + Status 'erfasst'/'korrektur_erforderlich' (Whitelist). */
  canManage: boolean;
  /** Nach Upload/Löschen: Drawer lädt neu + Event ans Grid. */
  onChanged: () => void;
  onError: (message: string) => void;
}

export function InvoiceAttachments({ invoiceId, attachments, canManage, onChanged, onError }: InvoiceAttachmentsProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [lightboxName, setLightboxName] = useState<string | null>(null);

  const handleUpload = (files: FileList | null) => {
    const list = Array.from(files ?? []);
    if (list.length === 0) return;
    setIsUploading(true);
    invoicesApi
      .uploadFiles(invoiceId, list)
      .then((response) => {
        const errors = response.data.data.errors;
        if (errors.length > 0) {
          onError(`Einige Anlagen fehlgeschlagen: ${errors.map((e) => e.name).join(', ')}`);
        }
        onChanged();
      })
      .catch((err) => onError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Hochladen'))
      .finally(() => setIsUploading(false));
  };

  const handleDelete = (file: InvoiceFile) => {
    if (!window.confirm(`Anlage "${file.original_name ?? file.id}" löschen?`)) return;
    invoicesApi
      .removeFile(invoiceId, file.id)
      .then(() => onChanged())
      .catch((err) => onError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Löschen'));
  };

  if (attachments.length === 0 && !canManage) {
    return <p className="text-sm text-gray-500">Keine Anlagen hochgeladen.</p>;
  }

  return (
    <div>
      {/* Upload */}
      {canManage && (
        <div className="mb-3">
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-primary-600 hover:text-primary-700">
            {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
            Anlagen hochladen
            <input
              type="file"
              multiple
              accept={ATTACHMENT_ACCEPT}
              className="hidden"
              onChange={(e) => {
                handleUpload(e.target.files);
                e.target.value = '';
              }}
            />
          </label>
          <p className="mt-1 text-xs text-gray-500">PDF, Bilder, CSV, Excel oder Word — maximal 10 MB je Datei.</p>
        </div>
      )}

      {attachments.length === 0 ? (
        <p className="text-sm text-gray-500">Keine Anlagen hochgeladen.</p>
      ) : (
        <ul className="space-y-2">
          {attachments.map((file) => (
            <InvoiceAttachmentItem
              key={file.id}
              invoiceId={invoiceId}
              file={file}
              canManage={canManage}
              onDelete={() => handleDelete(file)}
              onOpenImage={(url, name) => {
                setLightboxUrl(url);
                setLightboxName(name);
              }}
            />
          ))}
        </ul>
      )}

      {lightboxUrl && (
        <InvoiceImageLightbox
          url={lightboxUrl}
          fileName={lightboxName}
          onClose={() => {
            setLightboxUrl(null);
            setLightboxName(null);
          }}
        />
      )}
    </div>
  );
}

/** Eine Anlage: Thumbnail (Bild) / Inline-Vorschau (PDF) / Download (Rest). */
function InvoiceAttachmentItem({
  invoiceId, file, canManage, onDelete, onOpenImage,
}: {
  invoiceId: number;
  file: InvoiceFile;
  canManage: boolean;
  onDelete: () => void;
  onOpenImage: (url: string, name: string | null) => void;
}) {
  const group = mimeGroup(file.file_mime, file.original_name);
  const Icon = groupIcon[group];

  return (
    <li className="flex flex-col gap-2 rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-2">
      <div className="flex items-center gap-3">
        {group === 'image' ? (
          <AttachmentThumbnail
            invoiceId={invoiceId}
            fileId={file.id}
            fileName={file.original_name}
            onOpen={onOpenImage}
          />
        ) : (
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-white text-gray-500">
            <Icon className="h-5 w-5" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-gray-900" title={file.original_name ?? undefined}>
            {file.original_name || `Anlage #${file.id}`}
            {file.is_primary && <span className="ml-1.5 rounded bg-gray-200 px-1 text-[10px] text-gray-600">Original</span>}
          </p>
          <p className="text-xs text-gray-500">
            {formatFileSize(file.file_size)}
            {file.uploaded_by_name ? ` · ${file.uploaded_by_name}` : ''}
            {file.created_at ? ` · ${formatDateTime(file.created_at)}` : ''}
          </p>
        </div>
        <AttachmentActions invoiceId={invoiceId} file={file} group={group} canManage={canManage} onDelete={onDelete} />
      </div>
      {group === 'pdf' && <PdfPreviewToggle invoiceId={invoiceId} fileId={file.id} fileName={file.original_name} />}
    </li>
  );
}

/** Bild-Thumbnail: Blob-URL laden, strikt revoken (Cleanup). */
function AttachmentThumbnail({
  invoiceId, fileId, fileName, onOpen,
}: {
  invoiceId: number;
  fileId: number;
  fileName: string | null;
  onOpen: (url: string, name: string | null) => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setIsLoading(true);
    invoicesApi
      .getFileBlob(invoiceId, fileId)
      .then((response) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(response.data);
        setUrl(objectUrl);
        setIsLoading(false);
      })
      .catch(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [invoiceId, fileId]);

  if (isLoading) {
    return <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-white"><Loader2 className="h-4 w-4 animate-spin text-gray-400" /></span>;
  }
  if (!url) {
    return <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-white text-gray-400"><ImageIcon className="h-5 w-5" /></span>;
  }
  return (
    <button
      type="button"
      onClick={() => onOpen(url, fileName)}
      title="Bild vergrößern"
      className="shrink-0 overflow-hidden rounded"
    >
      <img src={url} alt={fileName ?? 'Anlage'} className="h-10 w-10 object-cover" />
    </button>
  );
}

/** Aktionen je Anlage: Herunterladen (Blob) + Löschen. */
function AttachmentActions({
  invoiceId, file, group, canManage, onDelete,
}: {
  invoiceId: number;
  file: InvoiceFile;
  group: MimeGroup;
  canManage: boolean;
  onDelete: () => void;
}) {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = () => {
    setIsDownloading(true);
    invoicesApi
      .getFileBlob(invoiceId, file.id)
      .then((response) => {
        const url = URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.original_name || `anlage-${file.id}`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => undefined)
      .finally(() => setIsDownloading(false));
  };

  return (
    <div className="flex shrink-0 items-center gap-1">
      {group !== 'image' && group !== 'pdf' && (
        <button
          type="button"
          onClick={handleDownload}
          disabled={isDownloading}
          title="Herunterladen"
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
        >
          {isDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        </button>
      )}
      {canManage && (
        <button
          type="button"
          onClick={onDelete}
          title="Anlage löschen"
          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

/** PDF: Inline-Vorschau aufklappen (Blob-URL im iframe, siehe InvoicePdfPreview). */
function PdfPreviewToggle({
  invoiceId, fileId, fileName,
}: {
  invoiceId: number;
  fileId: number;
  fileName: string | null;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="text-xs text-primary-600 hover:text-primary-700"
      >
        {isOpen ? 'Vorschau ausblenden' : 'Vorschau anzeigen'}
      </button>
      {isOpen && (
        <div className="mt-2">
          <InvoicePdfPreview invoiceId={invoiceId} fileId={fileId} fileName={fileName} />
        </div>
      )}
    </div>
  );
}